import { useEffect, useState } from 'react';
import { Controller, useWatch } from 'react-hook-form';
import { useAppForm } from '../../components/common/useAppForm';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { useSaveSubject, useSubject, type Subject } from '../../api/subjects';
import { useEnumLabel, useEnumOptions, useEnumParent } from '../../api/enums';
import { isApiError } from '../../api/errors';
import { BackLink, Button, ButtonGroup, ButtonLink, ErrorSummary, Input, RequiredFieldsNote, Select } from '../../components/idsk';
import { applyServerErrors, fieldError, summaryItems, type FieldOrder } from '../../components/common/FormErrors';
import { VersionConflictSummary } from '../../components/common/VersionConflict';
import { useNavigateWithToast } from '../../components/common/navigateWithToast';
import { PageHeading } from '../../layout/PageHeading';
import { usePageTitle } from '../../layout/usePageTitle';
import { normalizeEduid, normalizePsc } from '../../validation/rules';
import { subjectSchema, type SubjectFormValues } from '../../validation/schemas';
import { formatPsc } from '../../lib/format';
import { QueryError } from '../common/QueryError';
import { LoadingPage } from '../common/LoadingPage';
import { RequireAdmin } from '../common/RequireAdmin';
import { useAnnouncer } from '../../layout/Announcer';

const EMPTY: SubjectFormValues = {
  name: '',
  eduid: '',
  type: '',
  street: '',
  houseNumber: '',
  municipality: '',
  postalCode: '',
  region: '',
  district: '',
};

const ORDER: FieldOrder<SubjectFormValues>[] = [
  { name: 'name', id: 'subject-name' },
  { name: 'eduid', id: 'subject-eduid' },
  { name: 'type', id: 'subject-type' },
  { name: 'street', id: 'subject-street' },
  { name: 'houseNumber', id: 'subject-houseNumber' },
  { name: 'municipality', id: 'subject-municipality' },
  { name: 'postalCode', id: 'subject-postalCode' },
  { name: 'region', id: 'subject-region' },
  { name: 'district', id: 'subject-district' },
];

const SERVER_FIELDS: Record<string, keyof SubjectFormValues> = {
  name: 'name',
  eduid: 'eduid',
  type: 'type',
  region: 'region',
  district: 'district',
  'address.street': 'street',
  'address.houseNumber': 'houseNumber',
  'address.municipality': 'municipality',
  'address.postalCode': 'postalCode',
};

function toValues(s: Subject): SubjectFormValues {
  return {
    name: s.name,
    eduid: s.eduid,
    type: s.type,
    street: s.address.street,
    houseNumber: s.address.houseNumber,
    municipality: s.address.municipality,
    postalCode: formatPsc(s.address.postalCode),
    region: s.region,
    district: s.district ?? '',
  };
}

export function SubjectFormPage() {
  return (
    <RequireAdmin>
      <SubjectForm />
    </RequireAdmin>
  );
}

function SubjectForm() {
  const { id } = useParams();
  const editing = Boolean(id);
  const { t } = useTranslation();
  const existing = useSubject(id);
  const save = useSaveSubject(id);
  const navigateWithToast = useNavigateWithToast();
  const typeOptions = useEnumOptions('institutionTypes');
  const regionOptions = useEnumOptions('regions');
  const regionOf = useEnumParent();
  const label = useEnumLabel();
  const { announce } = useAnnouncer();
  const [conflict, setConflict] = useState(false);
  const [otherErrors, setOtherErrors] = useState<string[]>([]);
  const title = editing ? t('subjects.editTitle') : t('subjects.newTitle');
  usePageTitle([title, t('subjects.title')]);

  const form = useAppForm<SubjectFormValues>(subjectSchema, EMPTY);
  const { register, handleSubmit, formState, setError, reset, control, getValues, setValue } = form;
  const region = useWatch({ control, name: 'region' });
  // the districts of the selected region; all of them, grouped by region, until a region is selected
  const districtOptions = useEnumOptions('districts', region || undefined);

  /** A district of another region is cleared when the region changes (announced, focus stays on the region). */
  const changeRegion = (next: string) => {
    const district = getValues('district');
    if (district && next && regionOf('districts', district) !== next) {
      setValue('district', '');
      announce(t('subjects.districtCleared', { district: label('districts', district), region: label('regions', next) }));
    }
  };

  useEffect(() => {
    if (existing.data) reset(toValues(existing.data));
  }, [existing.data, reset]);

  if (editing && existing.error) return <QueryError error={existing.error} asPage />;
  if (editing && existing.isPending) return <LoadingPage />;

  const onSubmit = handleSubmit(async (values) => {
    setConflict(false);
    setOtherErrors([]);
    try {
      const saved = await save.mutateAsync({
        name: values.name,
        eduid: normalizeEduid(values.eduid),
        type: values.type,
        region: values.region,
        district: values.district || null,
        address: { street: values.street, houseNumber: values.houseNumber, municipality: values.municipality, postalCode: normalizePsc(values.postalCode) },
        version: existing.data?.version,
      });
      navigateWithToast(`/subjekty/${saved.id}`, t('subjects.saved', { name: saved.name }));
    } catch (e) {
      if (isApiError(e) && e.isVersionConflict) {
        setConflict(true);
        return;
      }
      const unmatched = applyServerErrors(e, setError, SERVER_FIELDS);
      setOtherErrors(unmatched.length || !isApiError(e) || e.fieldErrors.length === 0 ? [isApiError(e) ? e.message : t('errors.unexpected'), ...unmatched] : []);
    }
  });

  const reload = async () => {
    const fresh = await existing.refetch();
    if (fresh.data) reset(toValues(fresh.data));
    setConflict(false);
  };

  const errors = summaryItems(t, formState.errors, ORDER);
  const err = (name: keyof SubjectFormValues) => fieldError(t, formState.errors[name]);

  return (
    <>
      <BackLink to={editing ? `/subjekty/${id}` : '/subjekty'} />
      <div className="govuk-grid-row">
        <div className="govuk-grid-column-two-thirds">
          {conflict && <VersionConflictSummary onReload={() => void reload()} focusKey={formState.submitCount} />}
          {!conflict && (errors.length > 0 || otherErrors.length > 0) && (
            <ErrorSummary errors={[...errors, ...otherErrors.map((text) => ({ text }))]} focusKey={formState.submitCount} />
          )}
          <PageHeading>{title}</PageHeading>
          <form onSubmit={(e) => void onSubmit(e)} noValidate>
            <RequiredFieldsNote />
            <Input id="subject-name" label={t('subjects.fields.name')} required error={err('name')} {...register('name')} />
            <Input id="subject-eduid" label={t('subjects.fields.eduid')} required hint={t('subjects.hints.eduid')} width="10" inputMode="numeric" error={err('eduid')} {...register('eduid')} />
            <Controller
              control={control}
              name="type"
              render={({ field }) => (
                <Select
                  id="subject-type"
                  label={t('subjects.fields.type')}
                  required
                  hint={t('subjects.hints.type')}
                  options={typeOptions}
                  placeholder={t('common.select')}
                  error={err('type')}
                  width="full"
                  {...field}
                />
              )}
            />
            <fieldset className="govuk-fieldset" aria-describedby="subject-address-hint">
              <legend className="govuk-fieldset__legend govuk-fieldset__legend--m">
                <h2 className="govuk-fieldset__heading">{t('subjects.fields.address')}</h2>
              </legend>
              <span id="subject-address-hint" className="govuk-hint">
                {t('subjects.hints.address')}
              </span>
              <Input id="subject-street" label={t('subjects.fields.street')} required autoComplete="street-address" error={err('street')} {...register('street')} />
              <Input id="subject-houseNumber" label={t('subjects.fields.houseNumber')} required hint={t('subjects.hints.houseNumber')} width="10" error={err('houseNumber')} {...register('houseNumber')} />
              <Input id="subject-municipality" label={t('subjects.fields.municipality')} required autoComplete="address-level2" width="two-thirds" error={err('municipality')} {...register('municipality')} />
              <Input id="subject-postalCode" label={t('subjects.fields.postalCode')} required hint={t('subjects.hints.postalCode')} autoComplete="postal-code" width="5" inputMode="numeric" error={err('postalCode')} {...register('postalCode')} />
            </fieldset>
            <Controller
              control={control}
              name="region"
              render={({ field }) => (
                <Select
                  id="subject-region"
                  label={t('subjects.fields.region')}
                  required
                  options={regionOptions}
                  placeholder={t('common.select')}
                  error={err('region')}
                  width="full"
                  {...field}
                  onChange={(e) => {
                    field.onChange(e);
                    changeRegion(e.target.value);
                  }}
                />
              )}
            />
            <Controller
              control={control}
              name="district"
              render={({ field }) => (
                <Select
                  id="subject-district"
                  label={t('subjects.fields.district')}
                  hint={t('subjects.hints.district')}
                  options={districtOptions}
                  placeholder={t('common.select')}
                  error={err('district')}
                  width="full"
                  {...field}
                />
              )}
            />
            <ButtonGroup>
              <Button type="submit" preventDoubleClick>
                {t('common.save')}
              </Button>
              <ButtonLink to={editing ? `/subjekty/${id}` : '/subjekty'} variant="secondary">
                {t('common.cancel')}
              </ButtonLink>
            </ButtonGroup>
          </form>
        </div>
      </div>
    </>
  );
}
